/**
 * useGetGeography
 *
 * Single source of truth for the platform's ISO country list + US state map +
 * CA province map. Wraps GET /utils/geography (no auth required) in React Query
 * so multiple consumers (address forms, challenge sign-in modal's geo pickers,
 * any future country/state picker) dedupe to one network call per session.
 *
 * Consumers today:
 *   - web/src/components/modals/ChallengeLoginModal.tsx (eligibility geo gate)
 *
 * Existing direct callers of UtilsApi().getUtilsGetGeography (e.g.
 * web/src/components/inputs/the legacy address input.tsx) should migrate to
 * this hook when next touched — same payload, free deduping via React Query.
 */
import { useQuery } from "@tanstack/react-query";
import { UtilsApi } from "@devour/client";

export interface GeographyData {
    countries: Array<{ code: string; name: string }>;
    states: Record<string, string>;
    provinces?: Record<string, string>;
}

export function useGetGeography() {
    return useQuery<GeographyData>({
        queryKey: ["geography"],
        queryFn: async () => {
            const res = await new UtilsApi().getUtilsGetGeography();
            return {
                countries: (res.countries ?? []) as Array<{ code: string; name: string }>,
                states: (res.states ?? {}) as Record<string, string>,
                provinces: (res.provinces ?? {}) as Record<string, string>,
            };
        },
        staleTime: 24 * 60 * 60 * 1000,
        gcTime: 24 * 60 * 60 * 1000,
    });
}
